import React, { useEffect, useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { ipApi } from '../../services/api';

/* ═══════════════════════════════════════════════════════════
   ABHA / ABDM PATIENT PORTAL MODULE — Sandbox M1
═══════════════════════════════════════════════════════════ */

function profileToLinkRow(profile, extra = {}) {
  const p = profile?.ABHAProfile || profile || {};
  const abhaNum = p.ABHANumber || p.healthIdNumber || p.abhaNumber || '';
  const name = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') || p.name || extra.patient || '';
  return {
    abhaId: abhaNum,
    patient: name,
    uhid: extra.uhid || '',
    mobile: p.mobile || extra.mobile || '',
    dob: p.dob || '',
    gender: p.gender || '',
    phrAddress: (p.phrAddress || [])[0] || '',
    registryType: 'abdm-sandbox',
    status: 'Active',
    linkedAt: new Date().toISOString().slice(0, 10),
    ...extra,
  };
}

function SandboxSearchTab({ onLink }) {
  const [mobile, setMobile] = useState('');
  const [txnId, setTxnId] = useState('');
  const [mapped, setMapped] = useState([]);
  const [otp, setOtp] = useState('');
  const [profile, setProfile] = useState(null);
  const [uhid, setUhid] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  async function search(e) {
    e?.preventDefault();
    setErr('');
    setBusy('search');
    setProfile(null);
    setMapped([]);
    try {
      const data = await ipApi.abdmSearchAbha({ hint: 'mobile', mobile });
      setTxnId(data.txnId || '');
      setMapped(data.mappedABHAList || data.ABHAProfiles || []);
      if (!data.txnId && !(data.mappedABHAList || []).length) {
        setErr(data.message || 'No ABHA found for this mobile');
      }
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(''); }
  }

  async function sendOtp() {
    setErr('');
    setBusy('otp');
    try {
      const data = await ipApi.abdmLoginRequestOtp({
        txnId: txnId || undefined,
        hint: 'mobile',
        value: mobile,
      });
      if (data.txnId) setTxnId(data.txnId);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(''); }
  }

  async function verifyOtp(e) {
    e?.preventDefault();
    setErr('');
    setBusy('verify');
    try {
      let data = await ipApi.abdmLoginVerifyOtp({ txnId, otp });
      if (data.ABHAProfile) {
        setProfile(data);
      } else if (data.authResult?.ABHAProfile) {
        setProfile({ ABHAProfile: data.authResult.ABHAProfile });
      } else if (data.accounts?.length > 1) {
        data = await ipApi.abdmLoginVerifyUser({ txnId, abhaIndex: 0 });
        setProfile(data);
      } else {
        setProfile(data);
      }
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(''); }
  }

  async function saveLink() {
    if (!profile) return;
    const row = profileToLinkRow(profile, { uhid, mobile });
    if (!row.abhaId) { setErr('No ABHA number in response'); return; }
    await onLink(row);
    setProfile(null);
    setOtp('');
    setMapped([]);
    setTxnId('');
  }

  return (
    <div>
      <div className="info-banner mb">
        <strong>Sandbox M1</strong> — search and verify ABHA via national ABDM v3 APIs. Use sandbox test mobile/Aadhaar from NHA docs. Production ABHA requires NHA production approval.
      </div>
      <div className="card" style={{ borderLeft: '4px solid #6366f1' }}>
        <h3 style={{ marginTop: 0, color: '#4f46e5' }}>Find ABHA by mobile</h3>
        <form onSubmit={search} className="form-grid mb">
          <div className="field"><label>Mobile *</label>
            <input className="input" required value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit" />
          </div>
          <div className="field" style={{ alignSelf: 'end' }}>
            <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'search' ? 'Searching…' : 'Search ABHA'}</button>
          </div>
        </form>
        {mapped.length > 0 && (
          <div style={{ marginBottom: 12, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Accounts found ({mapped.length})</div>
            {mapped.map((m, i) => (
              <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0891b2' }}>{m.ABHANumber || m.abhaNumber}</span>
                {' · '}{m.name || m.fullName}
              </div>
            ))}
          </div>
        )}
        {txnId && (
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Txn: <code>{txnId}</code></div>
            <button type="button" className="btn btn-secondary btn-sm mb" onClick={sendOtp} disabled={!!busy}>
              {busy === 'otp' ? 'Sending…' : 'Send login OTP'}
            </button>
            <form onSubmit={verifyOtp} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
              <div className="field"><label>OTP</label>
                <input className="input" value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={!!busy || !otp}>{busy === 'verify' ? 'Verifying…' : 'Verify OTP'}</button>
            </form>
          </div>
        )}
        {profile && (
          <div style={{ marginTop: 16, padding: 16, background: 'linear-gradient(135deg, #4f46e5, #0891b2)', borderRadius: 14, color: '#fff' }}>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600, textTransform: 'uppercase' }}>Verified ABHA (sandbox)</div>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 900, letterSpacing: '0.1em', margin: '8px 0' }}>
              {(profile.ABHAProfile || profile).ABHANumber || '—'}
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {profileToLinkRow(profile).patient}
              {(profile.ABHAProfile || profile).mobile ? ` · ${(profile.ABHAProfile || profile).mobile}` : ''}
            </div>
            <div className="form-grid" style={{ marginTop: 12 }}>
              <div className="field"><label style={{ color: '#e0e7ff' }}>UHID (optional)</label>
                <input className="input" value={uhid} onChange={e => setUhid(e.target.value)} placeholder="Hospital UHID" />
              </div>
            </div>
            <button type="button" className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveLink}>Link to hospital records</button>
          </div>
        )}
        {err && <div style={{ marginTop: 12, color: '#dc2626', fontSize: 13 }}>{err}</div>}
      </div>
    </div>
  );
}

function SandboxCreateTab({ onLink }) {
  const [aadhaar, setAadhaar] = useState('');
  const [mobile, setMobile] = useState('');
  const [txnId, setTxnId] = useState('');
  const [otp, setOtp] = useState('');
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  async function requestOtp(e) {
    e?.preventDefault();
    setErr('');
    setBusy('otp');
    setProfile(null);
    try {
      const data = await ipApi.abdmEnrollmentRequestOtp({ aadhaar, txnId: txnId || undefined });
      setTxnId(data.txnId || txnId);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(''); }
  }

  async function enrol(e) {
    e?.preventDefault();
    setErr('');
    setBusy('enrol');
    try {
      const data = await ipApi.abdmEnrollmentEnrol({ txnId, otp, mobile: mobile || undefined });
      setProfile(data);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(''); }
  }

  async function saveLink() {
    if (!profile) return;
    const row = profileToLinkRow(profile, { mobile });
    if (!row.abhaId) { setErr('No ABHA number returned'); return; }
    await onLink(row);
    setProfile(null);
    setOtp('');
    setTxnId('');
    setAadhaar('');
  }

  return (
    <div>
      <div className="info-banner mb">
        Create new ABHA via <strong>Aadhaar OTP</strong> (sandbox enrollment). OTP is sent to the Aadhaar-linked mobile. Use only sandbox test credentials from NHA.
      </div>
      <div className="card" style={{ borderLeft: '4px solid #06b6d4' }}>
        <h3 style={{ marginTop: 0, color: '#0891b2' }}>Create ABHA (Aadhaar)</h3>
        <form onSubmit={requestOtp} className="form-grid mb">
          <div className="field"><label>Aadhaar *</label>
            <input className="input" required value={aadhaar} onChange={e => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="12-digit" />
          </div>
          <div className="field"><label>Contact mobile (optional)</label>
            <input className="input" value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="If different from Aadhaar" />
          </div>
          <div className="field" style={{ alignSelf: 'end' }}>
            <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'otp' ? 'Sending…' : 'Request Aadhaar OTP'}</button>
          </div>
        </form>
        {txnId && (
          <form onSubmit={enrol} style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Txn: <code>{txnId}</code></div>
            <div className="form-grid mb">
              <div className="field"><label>OTP from Aadhaar mobile</label>
                <input className="input" required value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} />
              </div>
              <div className="field" style={{ alignSelf: 'end' }}>
                <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'enrol' ? 'Creating…' : 'Create ABHA'}</button>
              </div>
            </div>
          </form>
        )}
        {profile?.ABHAProfile && (
          <div style={{ marginTop: 16, padding: 16, background: 'linear-gradient(135deg, #0891b2, #0d6e6a)', borderRadius: 14, color: '#fff' }}>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600 }}>ABHA created (sandbox)</div>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 900, margin: '8px 0' }}>{profile.ABHAProfile.ABHANumber}</div>
            <div style={{ fontSize: 13 }}>{profileToLinkRow(profile).patient}</div>
            {profile.isNew && <div style={{ fontSize: 12, marginTop: 6, opacity: 0.9 }}>New account</div>}
            <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={saveLink}>Link to hospital records</button>
          </div>
        )}
        {err && <div style={{ marginTop: 12, color: '#dc2626', fontSize: 13 }}>{err}</div>}
      </div>
    </div>
  );
}

function InternalIdTab({ linked, onLink }) {
  const [form, setForm] = useState({ name: '', mobile: '', dob: '', gender: 'M' });
  const [generated, setGenerated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linkForm, setLinkForm] = useState({ abhaId: '', patient: '', uhid: '' });

  async function generateInternal(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const id = `HREG-${String(Date.now()).slice(-8)}`;
      const row = { abhaId: id, name: form.name, mobile: form.mobile, gender: form.gender, dob: form.dob, registryType: 'internal' };
      await onLink({ ...row, patient: form.name, linkedAt: new Date().toISOString().slice(0, 10), status: 'Active' });
      setGenerated(row);
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="info-banner mb">
        Internal hospital registry ID (HREG-…) — not a government ABHA. Use sandbox tabs above for national ABHA.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Register internal ID</h3>
          <form onSubmit={generateInternal}>
            <div className="form-grid mb">
              <div className="field full"><label>Name *</label><input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="field"><label>Mobile</label><input className="input" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} /></div>
              <div className="field"><label>DOB</label><input type="date" className="input" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} /></div>
            </div>
            <button type="submit" className="btn btn-secondary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save internal ID'}</button>
          </form>
          {generated && <div style={{ marginTop: 12, fontFamily: 'monospace', fontWeight: 800, color: '#64748b' }}>{generated.abhaId}</div>}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Manual link (existing ABHA)</h3>
          <div className="form-grid mb">
            <div className="field full"><label>ABHA number</label><input className="input" value={linkForm.abhaId} onChange={e => setLinkForm(f => ({ ...f, abhaId: e.target.value }))} placeholder="91-XXXX-XXXX-XXXX" /></div>
            <div className="field"><label>Patient</label><input className="input" value={linkForm.patient} onChange={e => setLinkForm(f => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>UHID</label><input className="input" value={linkForm.uhid} onChange={e => setLinkForm(f => ({ ...f, uhid: e.target.value }))} /></div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { if (linkForm.abhaId && linkForm.patient) { onLink({ ...linkForm, registryType: 'manual', status: 'Active', linkedAt: new Date().toISOString().slice(0, 10) }); setLinkForm({ abhaId: '', patient: '', uhid: '' }); } }}>Link</button>
          <h4 style={{ marginTop: 16 }}>Linked records</h4>
          {linked.map(l => (
            <div key={l.abhaId || l.recordId} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0891b2' }}>{l.abhaId}</span>
              <span style={{ color: '#64748b' }}> · {l.patient}</span>
              {l.registryType && <span style={{ fontSize: 10, marginLeft: 6, color: '#94a3b8' }}>({l.registryType})</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConsentTab({ requests, onPatch }) {
  const sColor = { Pending: '#f59e0b', Approved: '#10b981', Rejected: '#dc2626' };
  return (
    <div>
      <div className="info-banner mb">
        Internal consent workflow — national ABDM Consent Manager (M2+) not connected yet.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map(r => (
          <div key={r.id || r.recordId} className="card" style={{ borderLeft: `4px solid ${sColor[r.status]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 800 }}>{r.patient} <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#0891b2' }}>{r.abhaId}</span></div>
                <div style={{ fontSize: 13, color: '#475569' }}>Requested by: {r.requestedBy}</div>
              </div>
              {r.status === 'Pending' && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => onPatch(r.recordId || r.id, { status: 'Approved' })}>Approve</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPatch(r.recordId || r.id, { status: 'Rejected' })}>Reject</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AbdmStatusTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ipApi.abdmStatus();
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const checks = status ? [
    { label: 'ABDM client credentials', status: status.configured, detail: status.configured ? `Client ${status.clientId} · ${status.environment}` : 'Set ABDM_CLIENT_ID and ABDM_CLIENT_SECRET in Lambda' },
    { label: 'Gateway session (v3)', status: status.sessionOk, detail: status.sessionOk ? `Token valid (~${status.expiresInSec}s)` : (status.sessionError || 'Session failed — check credentials') },
    { label: 'ABHA search / login OTP (M1)', status: status.m1?.search, detail: status.m1?.search ? 'Sandbox M1 routes active' : 'Requires valid sandbox session' },
    { label: 'ABHA enrollment (Aadhaar OTP)', status: status.m1?.enrollment, detail: status.m1?.enrollment ? 'Create ABHA via Aadhaar enabled' : 'Pending session' },
    { label: 'Health Facility Registry (HFR)', status: false, detail: 'Register facility in ABDM sandbox portal separately' },
    { label: 'Consent Manager (M2)', status: false, detail: 'Not implemented — internal workflow only' },
    { label: 'HIP / FHIR record push (M3)', status: false, detail: 'Planned after M1 validation' },
  ] : [];
  const passed = checks.filter(c => c.status).length;

  return (
    <div>
      {loading && <div className="info-banner mb">Loading ABDM status…</div>}
      {err && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{err}</div>}
      {status && (
        <>
          <div className={`info-banner mb`} style={{ background: status.sessionOk ? '#f0fdf4' : '#fffbeb', borderColor: status.sessionOk ? '#86efac' : '#fde68a' }}>
            {status.sessionOk
              ? <>ABDM <strong>sandbox</strong> connected — gateway <code>{status.gateway}</code></>
              : <>ABDM sandbox {status.configured ? 'credentials set but session failed' : 'not configured'}. Add env vars to Lambda <strong>HCEN_IP_API</strong>.</>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
            {[{ label: 'Live', val: passed, color: '#10b981' }, { label: 'Pending', val: checks.length - passed, color: '#f59e0b' }].map(s => (
              <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Integration checklist</h3>
            {checks.map(c => (
              <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{c.status ? '✓' : '○'} {c.label}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{c.detail}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: c.status ? '#16a34a' : '#ca8a04' }}>{c.status ? 'Live' : 'Pending'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AbhaPage() {
  const { records: linked, create: createLink } = useHmsRecords('abha-link');
  const { records: requests, patch } = useHmsRecords('abha-consent');
  const [tab, setTab] = useState('search');

  const linkAbha = async (row) => {
    await createLink({ ...row, linkedAt: row.linkedAt || new Date().toISOString().slice(0, 10), status: row.status || 'Active' });
  };

  return (
    <PageShell
      title="ABHA / ABDM Integration"
      subtitle="Sandbox M1 — search, verify, and create ABHA via ABDM v3"
      actions={<a href="https://sandbox.abdm.gov.in" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">ABDM Sandbox</a>}
    >
      <div style={{ padding: '10px 14px', background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#0c4a6e' }}>
        National ABHA uses ABDM v3 sandbox APIs proxied through your Lambda. Set <code>ABDM_CLIENT_ID</code> and <code>ABDM_CLIENT_SECRET</code> on Lambda, deploy <code>lambda\dist\HCEN_IP_API.zip</code>, then use Search or Create tabs below.
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'search', label: 'Search ABHA' },
          { id: 'create', label: 'Create ABHA' },
          { id: 'internal', label: 'Internal / Manual' },
          { id: 'consent', label: 'Consent' },
          { id: 'status', label: 'ABDM Status' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'search' && <SandboxSearchTab onLink={linkAbha} />}
      {tab === 'create' && <SandboxCreateTab onLink={linkAbha} />}
      {tab === 'internal' && <InternalIdTab linked={linked} onLink={linkAbha} />}
      {tab === 'consent' && <ConsentTab requests={requests} onPatch={patch} />}
      {tab === 'status' && <AbdmStatusTab />}
    </PageShell>
  );
}
